import React from 'react';

// Localized labels + static copy. The backend sends only data values (+ the
// `isActivation` flag + `language`); all UI text lives here, mirroring how
// PendingActionCards keeps its LABELS.
const LABELS = {
  en: {
    completed: 'Payment completed',
    descActivation:
      'You have completed your payment successfully and your request for Activation of Electricity/Water (Move-in) service has been submitted.',
    descGeneric: 'You have completed your payment successfully.',
    print: 'Print this page',
    referenceNumber: 'Reference Number',
    transactionDetails: 'Transaction Details',
    paymentDateTime: 'Payment Date and Time',
    partnerTransactionId: 'Partner Transaction ID',
    dewaTransactionId: 'DEWA Transaction ID',
    customerName: 'Customer Name',
    accountDetails: 'Account details',
    premise: 'DEWA Premise Number',
    contract: 'DEWA Contract Account Number',
    totalAmount: 'Total amount',
    whatsNext: "What's next?",
    whatsNextBody:
      'Your application is being processed and Electricity and Water service will be activated within 15 working hours.',
  },
  ar: {
    completed: 'اكتمل الدفع',
    descActivation:
      'لقد أتممت عملية الدفع بنجاح وتم تقديم طلبك لتفعيل خدمة الكهرباء والمياه (النقل إلى مسكن جديد).',
    descGeneric: 'لقد أتممت عملية الدفع بنجاح.',
    print: 'طباعة هذه الصفحة',
    referenceNumber: 'الرقم المرجعي',
    transactionDetails: 'تفاصيل المعاملة',
    paymentDateTime: 'تاريخ ووقت الدفع',
    partnerTransactionId: 'معرّف عملية الشريك',
    dewaTransactionId: 'معرّف عملية DEWA',
    customerName: 'اسم العميل',
    accountDetails: 'تفاصيل الحساب',
    premise: 'رقم عقار ديوا',
    contract: 'رقم حساب العقد لدى هيئة كهرباء ومياه دبي',
    totalAmount: 'المبلغ الإجمالي',
    whatsNext: 'ما الخطوة التالية؟',
    whatsNextBody: 'تتم معالجة طلبك وسيتم تفعيل خدمة الكهرباء والمياه خلال 15 ساعة عمل.',
  },
};

const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== '';

const formatAmount = (amount) =>
  (Number(amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Dirham glyph: the letter "D" in the bundled AED-Regular font (loaded globally
// by MessageItem). Falls back to the currency code for non-AED amounts.
const Money = ({ amount, currency }) => (
  <span dir="ltr" style={{ fontWeight: 700, whiteSpace: 'nowrap', color: '#007560' }}>
    {!currency || currency === 'AED' ? (
      <span style={{ fontFamily: "'AED-Regular', 'Arial', sans-serif", marginInlineEnd: '4px' }}>D</span>
    ) : (
      <span style={{ marginInlineEnd: '4px' }}>{currency}</span>
    )}
    {formatAmount(amount)}
  </span>
);

const CheckBadge = () => (
  <span style={{ width: '34px', height: '34px', borderRadius: '50%', backgroundColor: '#007560', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" aria-hidden="true">
      <path d="m5 12 5 5 9-11" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const PrinterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1f2937" strokeWidth="1.8" aria-hidden="true">
    <path d="M6 9V2h12v7" /><rect x="6" y="13" width="12" height="8" /><path d="M6 17H3v-6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6h-3" />
  </svg>
);

const PaymentReceiptCard = ({ data, isArabic: isArabicProp }) => {
  if (!data || typeof data !== 'object') return null;

  const isArabic = (data.language || (isArabicProp ? 'ar' : 'en')) === 'ar';
  const t = LABELS[isArabic ? 'ar' : 'en'];
  const dir = isArabic ? 'rtl' : 'ltr';
  const txn = data.transaction || {};
  const accounts = Array.isArray(data.accounts) ? data.accounts : [];

  const styles = {
    container: { direction: dir, width: '92%', margin: '8px auto', color: '#1a1a1a' },
    header: { backgroundColor: '#e8f3f0', borderRadius: '10px', padding: '12px 16px', textAlign: 'center' },
    checkWrap: { display: 'flex', justifyContent: 'center', marginBottom: '6px' },
    headTitle: { color: '#007560', fontSize: '19px', fontWeight: 700, margin: '0 0 4px' },
    headDesc: { color: '#374151', fontSize: '13px', lineHeight: 1.45, maxWidth: '460px', margin: '0 auto' },
    printBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#1f2937', fontSize: '13px', fontWeight: 600, marginTop: '8px' },
    refRow: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', margin: '22px 0' },
    refLabel: { color: '#4b5563', fontSize: '14px' },
    refValue: { fontSize: '22px', fontWeight: 700, color: '#1a1a1a', letterSpacing: '0.5px' },
    sectionTitle: { fontSize: '18px', fontWeight: 700, color: '#1a1a1a', margin: '18px 0 10px' },
    field: { margin: '10px 0' },
    fieldLabel: { color: '#374151', fontSize: '13px', marginBottom: '2px' },
    fieldValue: { color: '#007560', fontSize: '14px', fontWeight: 700 },
    acctRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', padding: '6px 0' },
    totalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', backgroundColor: '#e8f3f0', borderRadius: '6px', padding: '12px 14px', marginTop: '10px' },
    totalLabel: { fontWeight: 700, color: '#1a1a1a', fontSize: '14px' },
    whatsNext: { textAlign: 'center', marginTop: '26px' },
    whatsNextTitle: { fontSize: '18px', fontWeight: 700, color: '#1a1a1a', marginBottom: '10px' },
    whatsNextBody: { color: '#374151', fontSize: '13px', lineHeight: 1.5, maxWidth: '520px', margin: '0 auto' },
    ltrValue: { unicodeBidi: 'plaintext' },
  };

  const Field = ({ label, value }) =>
    hasValue(value) ? (
      <div style={styles.field}>
        <div style={styles.fieldLabel}>{label}</div>
        <div style={styles.fieldValue} dir="ltr">{value}</div>
      </div>
    ) : null;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.checkWrap}><CheckBadge /></div>
        <div style={styles.headTitle}>{t.completed}</div>
        <div style={styles.headDesc}>{data.isActivation ? t.descActivation : t.descGeneric}</div>
        <button type="button" style={styles.printBtn} onClick={() => window.print()}>
          {t.print}
          <PrinterIcon />
        </button>
      </div>

      {hasValue(data.referenceNumber) && (
        <div style={styles.refRow}>
          <span style={styles.refLabel}>{t.referenceNumber}</span>
          <span style={styles.refValue} dir="ltr">{data.referenceNumber}</span>
        </div>
      )}

      <div style={styles.sectionTitle}>{t.transactionDetails}</div>
      <Field label={t.paymentDateTime} value={txn.paymentDateTime} />
      <Field label={t.partnerTransactionId} value={txn.partnerTransactionId} />
      <Field label={t.dewaTransactionId} value={txn.dewaTransactionId} />
      <Field label={t.customerName} value={txn.customerName} />

      {(accounts.length > 0 || hasValue(data.totalAmount)) && (
        <>
          <div style={styles.sectionTitle}>{t.accountDetails}</div>
          {accounts.map((acct, idx) => (
            <div style={styles.acctRow} key={acct.accountNumber || idx}>
              <div>
                <div style={styles.fieldLabel}>{acct.accountType === 'premise' ? t.premise : t.contract}</div>
                {hasValue(acct.accountNumber) && (
                  <div style={styles.fieldValue} dir="ltr">{acct.accountNumber}</div>
                )}
              </div>
              <Money amount={acct.amount} currency={acct.currency} />
            </div>
          ))}
          <div style={styles.totalRow}>
            <span style={styles.totalLabel}>{t.totalAmount}</span>
            <Money amount={data.totalAmount} currency={data.currency} />
          </div>
        </>
      )}

      {data.isActivation && (
        <div style={styles.whatsNext}>
          <div style={styles.whatsNextTitle}>{t.whatsNext}</div>
          <div style={styles.whatsNextBody}>{t.whatsNextBody}</div>
        </div>
      )}
    </div>
  );
};

export default PaymentReceiptCard;
